import { useReducer, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { translateForm } from 'utilities/form-utils'
import toast from 'utilities/toast'

/**
 * useProgressiveForm - Manages multi-step form state for progressive/wizard-style forms
 *
 * This hook enables backend-driven multi-step forms where the backend controls the flow
 * by returning `nextForm.keepOverlayOpen: true` in the response. This allows unlimited
 * form steps without frontend changes - the backend simply returns the next form configuration.
 *
 * Flow:
 * 1. User submits step 1 → Backend validates and returns nextForm with keepOverlayOpen: true
 * 2. Hook updates form state with new fieldsets/endpoint → User sees step 2
 * 3. Repeat until backend returns keepOverlayOpen: false (or omits it) → Form completes
 *
 * Data from previous steps is automatically preserved and merged with each submission,
 * ensuring the final submission contains all accumulated data.
 *
 * @example
 * const { fieldsets, endpoint, wrapOnSubmit } = useProgressiveForm({
 *   initialFieldsets: form.fieldsets,
 *   initialEndpoint: form.endpoint,
 *   isProgressive: form.isProgressive,
 * })
 */

const PROGRESSIVE_FORM_ACTIONS = {
  INIT: 'INIT',
  NEXT_STEP: 'NEXT_STEP',
}

function progressiveFormReducer(state, action) {
  switch (action.type) {
    case PROGRESSIVE_FORM_ACTIONS.INIT:
      return {
        fieldsets: action.fieldsets,
        endpoint: action.endpoint,
        submitText: action.submitText,
        data: action.data,
        isProgressive: action.isProgressive,
        // Form behavior flags - can be updated by nextForm in progressive flows
        generatesDownloadableFiles: action.generatesDownloadableFiles,
        reload: action.reload,
        redirect: action.redirect,
      }
    case PROGRESSIVE_FORM_ACTIONS.NEXT_STEP:
      return {
        fieldsets: action.fieldsets,
        endpoint: action.endpoint || state.endpoint,
        submitText: action.submitText || state.submitText,
        // Merge previous data with submitted values and any additional data from backend
        data: {
          ...state.data,
          ...action.submittedValues,
          ...action.additionalData,
        },
        // Keep progressive flag - backend controls continuation via keepOverlayOpen
        isProgressive: state.isProgressive,
        // Update form behavior flags from nextForm (use nextForm values, fallback to current state)
        generatesDownloadableFiles:
          action.generatesDownloadableFiles ?? state.generatesDownloadableFiles,
        reload: action.reload ?? state.reload,
        redirect: action.redirect ?? state.redirect,
      }
    default:
      return state
  }
}

export function useProgressiveForm({
  initialFieldsets,
  initialEndpoint,
  initialSubmitText,
  initialData,
  isProgressive = false,
  initialGeneratesDownloadableFiles = false,
  initialReload = false,
  initialRedirect = null,
}) {
  const { t } = useTranslation()

  const [formState, dispatch] = useReducer(progressiveFormReducer, null, () => {
    const translatedFieldsets = translateForm(initialFieldsets || [], t)
    return {
      fieldsets: translatedFieldsets,
      endpoint: initialEndpoint,
      submitText: initialSubmitText,
      data: initialData,
      isProgressive,
      generatesDownloadableFiles: initialGeneratesDownloadableFiles,
      reload: initialReload,
      redirect: initialRedirect,
    }
  })

  // Re-initialize when props change (e.g., when parent component receives new form config)
  useEffect(() => {
    const translatedFieldsets = translateForm(initialFieldsets || [], t)
    dispatch({
      type: PROGRESSIVE_FORM_ACTIONS.INIT,
      fieldsets: translatedFieldsets,
      endpoint: initialEndpoint,
      submitText: initialSubmitText,
      data: initialData,
      isProgressive,
      generatesDownloadableFiles: initialGeneratesDownloadableFiles,
      reload: initialReload,
      redirect: initialRedirect,
    })
  }, [
    initialFieldsets,
    initialEndpoint,
    initialSubmitText,
    initialData,
    isProgressive,
    initialGeneratesDownloadableFiles,
    initialReload,
    initialRedirect,
    t,
  ])

  /**
   * Wraps the original onSubmit to intercept responses and handle progressive form transitions.
   * When backend returns nextForm.keepOverlayOpen: true, updates form state instead of completing.
   */
  const wrapOnSubmit = (originalOnSubmit) => {
    if (!originalOnSubmit) {
      return originalOnSubmit
    }

    return async (values, ...args) => {
      const endpoint = formState.endpoint || initialEndpoint
      const response = await originalOnSubmit(values, endpoint, ...args)
      return handleProgressiveResponse(response, values)
    }
  }

  /**
   * Checks if backend wants to continue to next step (keepOverlayOpen: true).
   * If so, updates form state with new fieldsets and returns _progressiveContinue marker.
   * If not, returns response unchanged for normal completion handling.
   */
  const handleProgressiveResponse = (response, submittedValues) => {
    const nextForm = response?.data?.nextForm

    if (nextForm?.keepOverlayOpen === true) {
      const translatedNextFieldsets = translateForm(nextForm.fieldsets || [], t)

      dispatch({
        type: PROGRESSIVE_FORM_ACTIONS.NEXT_STEP,
        fieldsets: translatedNextFieldsets,
        endpoint: nextForm.endpoint,
        submitText: nextForm.submitText,
        submittedValues,
        additionalData: response.data.additionalData,
        // Pass form behavior flags from nextForm
        generatesDownloadableFiles: nextForm.generatesDownloadableFiles,
        reload: nextForm.reload,
        redirect: nextForm.redirect,
      })

      const stepMessage =
        response._notification?.text || nextForm.message || response.message
      if (stepMessage) {
        toast.formNotification(response._notification, stepMessage)
      }

      // Signal to the calling component that form should continue, not complete
      return { ...response, _progressiveContinue: true }
    }

    return response
  }

  return {
    fieldsets: formState.fieldsets,
    endpoint: formState.endpoint,
    submitText: formState.submitText,
    data: formState.data,
    isProgressive: formState.isProgressive,
    // Form behavior flags - updated by nextForm in progressive flows
    generatesDownloadableFiles: formState.generatesDownloadableFiles,
    reload: formState.reload,
    redirect: formState.redirect,
    wrapOnSubmit,
    dispatch,
  }
}
